import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tabs as UnderlineTabs,
  Tab as UnderlineTab,
  Button as CrmButton,
  CrmDropdown,
  FormField,
  crmInputClass,
} from '@/components/crm-ui';
import { cn } from '@/lib/utils';
import {
  FloatingDrawer,
  FloatingDrawerBody,
  FloatingDrawerFooter,
  FloatingDrawerHeader,
} from '@/components/ui/floating-drawer';
import { EmptyTable, StatusPill, TableShell, ToolbarSearch } from '@/components/commerce-v2/shared';
import { BoardFilterDropdown } from '@/components/crm/BoardFilterDropdown';
import { UNKNOWN_META, type PillMeta } from '@/lib/commerce-v2/meta';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  IntegrationDataSync,
  type IntegrationSyncRow,
} from '@/components/integrations/IntegrationDataSync';
import {
  IntegrationSetupSteps,
  SetupCode,
  type IntegrationSetupStep,
} from '@/components/integrations/IntegrationSetupSteps';
import { Building2, CalendarDays, Hash, MoreHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useWorkspaceScopeId } from '@/hooks/useWorkspaceScopeId';
import { razorpayApi } from '@/lib/api/services/razorpay';
import { absoluteApiUrl } from '@/lib/api/absoluteApiUrl';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { validatePaymentAmount, MAX_PAYMENT_LINK_AMOUNT } from '@/lib/constants/paymentLimits';
import { Loader2, RefreshCw, PlugZap, Unplug, ArrowLeft, Key, Link as LinkIcon, ExternalLink, Copy, CreditCard, CalendarClock, Package, Play, Pause, Ban, Plus, Webhook, Check, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { ContactsPagination } from '@/components/contacts/ContactsPagination';
import { Checkbox } from '@/components/ui/checkbox';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';
import { motion } from 'framer-motion';

type Payment = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method?: string;
  payment_method?: string;
  description?: string;
  created_at: string | number | Date;
  order_id?: string;
  customer_id?: string;
  razorpay_payment_id?: string;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_contact?: string | null;
  contact?: {
    name?: string;
    phone?: string;
    email?: string;
  };
};

type PaymentLink = {
  id: string;
  short_url: string;
  amount: number;
  currency: string;
  description?: string;
  status: string;
  created_at: string | number | Date;
  updated_at?: string | number | Date;
  amount_paid?: number;
  customer?: {
    name?: string;
    email?: string;
    contact?: string;
  } | [];
};

type Plan = {
  id: string;
  entity: string;
  interval: number;
  period: string;
  item: {
    id: string;
    active: boolean;
    amount: number;
    unit_amount: number;
    currency: string;
    name: string;
    description?: string;
  };
  notes: Record<string, any>;
  created_at: number;
};

type Subscription = {
  id: string;
  entity: string;
  plan_id: string;
  customer_id?: string;
  status: string;
  current_start?: number | null;
  current_end?: number | null;
  ended_at?: number | null;
  quantity: number;
  notes: Record<string, any>;
  charge_at?: number;
  remaining_count?: number;
  paid_count?: number;
  total_count?: number;
  customer_notify?: number;
  created_at: number;
  short_url?: string;
  has_scheduled_changes?: boolean;
  payment_method?: string;
  offer_id?: string | null;
};

interface RazorpayPageProps {
  defaultTab?: string;
  hideTabs?: boolean;
  embedded?: boolean;
  refreshSignal?: number;
  testSignal?: number;
}

const RAZORPAY_WEBHOOK_URL = absoluteApiUrl('/webhooks/razorpay');

const RAZORPAY_WEBHOOK_CORE_EVENTS = [
  'payment.captured',
  'payment.authorized',
  'payment.failed',
  'order.paid',
  'payment_link.paid',
  'payment_link.expired',
  'payment_link.cancelled',
  'payment.refunded',
];

const RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS = [
  'subscription.authenticated',
  'subscription.activated',
  'subscription.charged',
  'subscription.cancelled',
  'subscription.completed',
  'subscription.expired',
  'invoice.paid',
  'invoice.payment_failed',
];

const WebhookStepNumber: React.FC<{ n: number }> = ({ n }) => (
  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 text-gray-400 font-bold">
    {n}
  </span>
);

const WebhookSetupCard: React.FC<{ variant: 'pre-connect' | 'post-connect' }> = ({ variant }) => {
  const [copied, setCopied] = useState<'url' | 'events' | null>(null);

  const copyValue = async (text: string, kind: 'url' | 'events') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied((c) => (c === kind ? null : c)), 2000);
      toast({
        variant: 'success',
        title: 'Copied',
        description: kind === 'url' ? 'Webhook URL copied to clipboard' : 'Event list copied to clipboard',
      });
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy it manually.', variant: 'destructive' });
    }
  };

  const allEvents = [...RAZORPAY_WEBHOOK_CORE_EVENTS, ...RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS].join(', ');

  return (
    <div className="bg-white rounded-[12px] p-6 md:p-8 space-y-5 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
      <div className="flex items-start justify-between gap-3 border-b border-gray-50 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand">
            <Webhook className="h-4 w-4" />
          </div>
          <div>
            <h4 className="font-bold text-base text-[#08090A]">Set up payment confirmations</h4>
            <p className="text-xs text-gray-500 mt-0.5">Add this webhook so payment status updates automatically</p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={
            variant === 'post-connect'
              ? 'bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap flex-shrink-0'
              : 'bg-gray-50 text-gray-500 border-gray-200 whitespace-nowrap flex-shrink-0'
          }
        >
          {variant === 'post-connect' ? 'Action required' : 'Step 2'}
        </Badge>
      </div>

      {variant === 'post-connect' && (
        <div className="flex gap-3 rounded-[10px] bg-amber-50/60 border border-amber-200/60 p-3.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-amber-800">
            Without this webhook, payments are still collected but their status won&apos;t update in WeNext and
            automation flows waiting on payment won&apos;t resume. This is a one-time setup.
          </p>
        </div>
      )}

      <ol className="space-y-4">
        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={1} />
          <p className="pt-0.5 leading-relaxed">
            Open your{' '}
            <a
              href="https://dashboard.razorpay.com/app/webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-bold hover:underline"
            >
              Razorpay Dashboard → Webhooks
            </a>{' '}
            (Account &amp; Settings → Webhooks).
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={2} />
          <p className="pt-0.5 leading-relaxed">
            Click <span className="font-semibold text-gray-800">+ Add New Webhook</span>.
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={3} />
          <div className="pt-0.5 flex-1 min-w-0 space-y-2">
            <p className="leading-relaxed">
              Paste this into the <span className="font-semibold text-gray-800">Webhook URL</span> field:
            </p>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 min-w-0 truncate rounded-[10px] bg-gray-50 border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800">
                {RAZORPAY_WEBHOOK_URL}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto rounded-[10px] flex-shrink-0"
                onClick={() => copyValue(RAZORPAY_WEBHOOK_URL, 'url')}
                title="Copy webhook URL"
              >
                {copied === 'url' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={4} />
          <p className="pt-0.5 leading-relaxed">
            Leave the <span className="font-semibold text-gray-800">Secret</span> field empty — WeNext verifies every
            event automatically, so no signing secret is needed here.
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={5} />
          <div className="pt-0.5 flex-1 min-w-0 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="leading-relaxed">
                Under <span className="font-semibold text-gray-800">Active Events</span>, enable:
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs flex-shrink-0"
                onClick={() => copyValue(allEvents, 'events')}
                title="Copy all event names"
              >
                {copied === 'events' ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                Copy all
              </Button>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Payments &amp; links (required)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RAZORPAY_WEBHOOK_CORE_EVENTS.map((ev) => (
                  <code
                    key={ev}
                    className="rounded-md bg-brand/5 border border-brand/15 px-2 py-1 font-mono text-[11px] text-[#0f7a48]"
                  >
                    {ev}
                  </code>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Subscriptions (only if you use recurring billing)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS.map((ev) => (
                  <code
                    key={ev}
                    className="rounded-md bg-gray-50 border border-gray-200 px-2 py-1 font-mono text-[11px] text-gray-600"
                  >
                    {ev}
                  </code>
                ))}
              </div>
            </div>
          </div>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={6} />
          <p className="pt-0.5 leading-relaxed">
            Click <span className="font-semibold text-gray-800">Create Webhook</span>. Payment confirmations are now
            live.
          </p>
        </li>
      </ol>
    </div>
  );
};

const RAZORPAY_TABS: { id: string; label: string }[] = [
  { id: 'connect', label: 'Account' },
  { id: 'payments', label: 'Payments' },
  { id: 'links', label: 'Payment Links' },
  { id: 'plans', label: 'Plans' },
  { id: 'subscriptions', label: 'Subscriptions' },
];

const RazorpayWebhookSetupSteps: React.FC = () => {
  const [copied, setCopied] = useState<'url' | 'events' | null>(null);

  const copyValue = async (text: string, kind: 'url' | 'events') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied((c) => (c === kind ? null : c)), 2000);
      toast({
        variant: 'success',
        title: 'Copied',
        description: kind === 'url' ? 'Webhook URL copied to clipboard' : 'Event list copied to clipboard',
      });
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy it manually.', variant: 'destructive' });
    }
  };

  const allEvents = [...RAZORPAY_WEBHOOK_CORE_EVENTS, ...RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS].join(', ');

  const steps: IntegrationSetupStep[] = [
    {
      title: 'Open Webhooks in the Razorpay Dashboard',
      body: (
        <>
          Go to{' '}
          <a
            href="https://dashboard.razorpay.com/app/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#067647] hover:underline"
          >
            Account &amp; Settings → Webhooks
          </a>{' '}
          and choose <SetupCode>+ Add New Webhook</SetupCode>.
        </>
      ),
    },
    {
      title: 'Paste the webhook URL',
      body: (
        <div className="mt-1.5 flex items-stretch gap-2">
          <code className="min-w-0 flex-1 truncate rounded-[8px] border border-[#EAECF0] bg-[#F9FAFB] px-3 py-2 font-mono text-[12.5px] text-[#101828]">
            {RAZORPAY_WEBHOOK_URL}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto shrink-0"
            onClick={() => copyValue(RAZORPAY_WEBHOOK_URL, 'url')}
            aria-label="Copy webhook URL"
          >
            {copied === 'url' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ),
    },
    {
      title: 'Leave the Secret field empty',
      body: 'WeNext verifies every event itself, so no signing secret is needed here.',
    },
    {
      title: 'Enable the events',
      body: (
        <div className="mt-1.5 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <span>Payments and links are required; subscriptions only if you bill on a schedule.</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs"
              onClick={() => copyValue(allEvents, 'events')}
            >
              {copied === 'events' ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
              Copy all
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {RAZORPAY_WEBHOOK_CORE_EVENTS.map((ev) => (
              <code
                key={ev}
                className="rounded-md border border-[#ABEFC6] bg-[#ECFDF3] px-2 py-0.5 font-mono text-[11.5px] text-[#067647]"
              >
                {ev}
              </code>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS.map((ev) => (
              <code
                key={ev}
                className="rounded-md border border-[#EAECF0] bg-[#F9FAFB] px-2 py-0.5 font-mono text-[11.5px] text-[#475467]"
              >
                {ev}
              </code>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Create the webhook',
      body: 'Choose Create Webhook. Payment confirmations are live from then on.',
    },
  ];

  return (
    <div className="space-y-4">
      <IntegrationSetupSteps title="Payment confirmations" steps={steps} />
      <p className="rounded-[10px] border border-[#FEDF89] bg-[#FFFAEB] px-3.5 py-2.5 text-[13px] leading-relaxed text-[#B54708]">
        Without this webhook, payments are still collected but their status won&apos;t update in WeNext,
        and automation flows waiting on a payment won&apos;t resume.
      </p>
    </div>
  );
};

const RZP_GREEN = { color: '#076A2D', bg: '#E7F7EC' };
const RZP_AMBER = { color: '#B54708', bg: '#FFFAEB' };
const RZP_RED = { color: '#B42318', bg: '#FEF3F2' };
const RZP_GREY = { color: '#475467', bg: '#F2F4F7' };
const RZP_MUTED = { color: '#667085', bg: '#F2F4F7' };
const RZP_BLUE = { color: '#175CD3', bg: '#EFF8FF' };

const RAZORPAY_STATUS_META: Record<string, PillMeta> = {
  captured: { label: 'Paid', ...RZP_GREEN },
  paid: { label: 'Paid', ...RZP_GREEN },
  active: { label: 'Active', ...RZP_GREEN },
  completed: { label: 'Completed', ...RZP_GREEN },
  authorized: { label: 'Authorized', ...RZP_BLUE },
  authenticated: { label: 'Authenticated', ...RZP_BLUE },
  pending: { label: 'Pending', ...RZP_AMBER },
  partially_paid: { label: 'Partially paid', ...RZP_AMBER },
  paused: { label: 'Paused', ...RZP_AMBER },
  created: { label: 'Created', ...RZP_GREY },
  refunded: { label: 'Refunded', ...RZP_GREY },
  expired: { label: 'Expired', ...RZP_MUTED },
  failed: { label: 'Failed', ...RZP_RED },
  cancelled: { label: 'Cancelled', ...RZP_RED },
  halted: { label: 'Halted', ...RZP_RED },
};

const razorpayStatusMeta = (status: string | null | undefined): PillMeta => {
  const key = (status || '').toLowerCase();
  const known = RAZORPAY_STATUS_META[key];
  if (known) return known;
  const label = key ? key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : UNKNOWN_META.label;
  return { ...UNKNOWN_META, label };
};

const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP'].map((c) => ({ value: c, label: c }));

const PLAN_PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const PAYMENT_STATUS_OPTIONS = [
  { id: 'captured', label: 'Paid' },
  { id: 'authorized', label: 'Authorized' },
  { id: 'pending', label: 'Pending' },
  { id: 'failed', label: 'Failed' },
  { id: 'refunded', label: 'Refunded' },
];

const ToolbarMoreMenu: React.FC<{
  label: string;
  items: {
    label: string;
    icon: React.ElementType;
    onSelect: () => void;
    disabled?: boolean;
    spinning?: boolean;
  }[];
}> = ({ label, items }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        title="More"
        aria-label={label}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E4E7EC] bg-white text-[#475467] transition hover:bg-[#F9FAFB] hover:text-[#101828]"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      sideOffset={6}
      className="w-[220px] rounded-xl border border-[#E4E7EC] bg-white p-1.5 shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
    >
      {items.map((item) => (
        <DropdownMenuItem
          key={item.label}
          disabled={item.disabled}
          onClick={() => item.onSelect()}
          className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#101828] hover:bg-[#F9FAFB] focus:bg-[#F9FAFB] focus:text-[#101828]"
        >
          <item.icon
            className={item.spinning ? 'h-4 w-4 animate-spin text-[#6B7280]' : 'h-4 w-4 text-[#6B7280]'}
          />
          {item.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const RazorpayPage: React.FC<RazorpayPageProps> = ({
  defaultTab = 'connect',
  hideTabs = false,
  embedded = false,
  refreshSignal = 0,
  testSignal = 0,
}) => {
  const { isLoading: accountsLoading } = useBusinessAccountContext();
  const workspaceScopeId = useWorkspaceScopeId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [form, setForm] = useState({
    keyId: '',
    keySecret: '',
  });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingData, setLoadingData] = useState(!!workspaceScopeId);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    connectionType?: 'manual' | 'oauth';
    accountId?: string;
    accountName?: string;
    expiresAt?: string;
    connectedAt?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('pstatus') || 'all');
  const [searchTerm, setSearchTerm] = useState<string>(() => searchParams.get('psearch') || '');

  const readIntParam = (key: string, fallback: number) => {
    const parsed = parseInt(searchParams.get(key) || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };
  const [paymentsPage, setPaymentsPage] = useState(() => readIntParam('ppage', 1));
  const [paymentsPageSize, setPaymentsPageSize] = useState(() => readIntParam('psize', 10));
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [linksPage, setLinksPage] = useState(() => readIntParam('lpage', 1));
  const [linksPageSize, setLinksPageSize] = useState(() => readIntParam('lsize', 10));
  const [linksTotal, setLinksTotal] = useState(0);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansPage, setPlansPage] = useState(() => readIntParam('plpage', 1));
  const [plansPageSize, setPlansPageSize] = useState(() => readIntParam('plsize', 10));
  const [plansTotal, setPlansTotal] = useState(0);
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: '',
    amount: '',
    currency: 'INR',
    description: '',
    period: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'yearly',
    interval: '1',
  });

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [offersById, setOffersById] = useState<Record<string, string>>({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subsPage, setSubsPage] = useState(() => readIntParam('spage', 1));
  const [subsPageSize, setSubsPageSize] = useState(() => readIntParam('ssize', 10));
  const [subsTotal, setSubsTotal] = useState(0);
  const [createSubDialogOpen, setCreateSubDialogOpen] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);
  const [subForm, setSubForm] = useState({
    planId: '',
    totalCount: '',
    quantity: '1',
    customerId: '',
    offerId: '',
  });

  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({
    amount: '',
    currency: 'INR',
    description: '',
    customerName: '',
    customerEmail: '',
    customerContact: '',
    notifySms: true,
    notifyEmail: true,
    reminderEnable: true,
  });
  const [creatingLink, setCreatingLink] = useState(false);

  const linkAmountCheck = validatePaymentAmount(linkForm.amount);
  const linkAmountError =
    linkForm.amount.trim() === '' || linkAmountCheck.ok ? null : linkAmountCheck.error;

  const rawTab = searchParams.get('tab') || defaultTab;
  const activeTab = (hideTabs && !isConnected && !loadingData && !accountsLoading) ? 'connect' : rawTab;
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  useEffect(() => {
    resetState();
    if (workspaceScopeId) {
      checkConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceScopeId]);

  const prevAccountRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevAccountRef.current;
    prevAccountRef.current = workspaceScopeId;
    if (prev !== undefined && prev !== null && prev !== workspaceScopeId) {
      setPaymentsPageSize(10);
      setLinksPageSize(10);
      setPlansPageSize(10);
      setSubsPageSize(10);
      setPaymentsPage(1);
      setLinksPage(1);
      setPlansPage(1);
      setSubsPage(1);
    }
  }, [workspaceScopeId]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const sync = (key: string, value: number, def: number) => {
          if (value === def) next.delete(key);
          else next.set(key, String(value));
        };
        sync('ppage', paymentsPage, 1);
        sync('psize', paymentsPageSize, 10);
        sync('lpage', linksPage, 1);
        sync('lsize', linksPageSize, 10);
        sync('plpage', plansPage, 1);
        sync('plsize', plansPageSize, 10);
        sync('spage', subsPage, 1);
        sync('ssize', subsPageSize, 10);
        const syncStr = (key: string, value: string, def: string) => {
          if (!value || value === def) next.delete(key);
          else next.set(key, value);
        };
        syncStr('pstatus', statusFilter, 'all');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsPage, paymentsPageSize, linksPage, linksPageSize, plansPage, plansPageSize, subsPage, subsPageSize, statusFilter]);

  const lastSearchedTermRef = useRef(searchTerm);
  useEffect(() => {
    if (lastSearchedTermRef.current === searchTerm) return;
    lastSearchedTermRef.current = searchTerm;
    if (!workspaceScopeId || !isConnected || activeTab !== 'payments') return;

    const timeoutId = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchTerm) next.set('psearch', searchTerm);
        else next.delete('psearch');
        return next;
      }, { replace: true });
      loadPayments();
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (workspaceScopeId && isConnected) {
      if (activeTab === 'payments') {
        loadPayments();
      } else if (activeTab === 'links') {
        loadPaymentLinks();
      } else if (activeTab === 'plans') {
        loadPlans();
      } else if (activeTab === 'subscriptions') {
        loadSubscriptions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceScopeId, activeTab, paymentsPage, paymentsPageSize, linksPage, linksPageSize, plansPage, plansPageSize, subsPage, subsPageSize, statusFilter, isConnected]);

  const resetState = () => {
    setIsConnected(false);
    setConnectionInfo(null);
    setError(null);
    setPayments([]);
    setPaymentLinks([]);
    setInitialLoadComplete(false); // Reset initial load flag
  };

  const checkConnection = async () => {
    if (!workspaceScopeId) return;
    setLoadingData(true);
    setError(null);
    try {
      const res = await razorpayApi.checkConnection(workspaceScopeId);
      if (res.connected) {
        setIsConnected(true);
        setConnectionInfo({
          connectionType: res.connectionType,
          accountId: res.accountId,
          accountName: res.accountName,
          expiresAt: res.expiresAt,
          connectedAt: res.connectedAt,
        });
      } else {
        setIsConnected(false);
        setConnectionInfo(null);
      }
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to check connection'));
      setIsConnected(false);
      setConnectionInfo(null);
    } finally {
      setLoadingData(false);
    }
  };

  const handleManualConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceScopeId) {
      toast({
        title: 'Select a business account',
        description: 'Please select a business account',
        variant: 'warning',
      });
      return;
    }

    setConnecting(true);
    setError(null);
    try {
      const result = await razorpayApi.connect({
        businessId: workspaceScopeId,
        keyId: form.keyId,
        keySecret: form.keySecret,
      });

      if (result.connected) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Razorpay connected successfully',
        });
        setForm({ keyId: '', keySecret: '' });
        await checkConnection();
        autoSyncData();
      }
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to connect Razorpay'));
      showApiError(err, { fallback: { title: 'Failed to connect Razorpay' } });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    if (!workspaceScopeId) return;

    setDisconnecting(true);
    setError(null);
    try {
      await razorpayApi.disconnect(workspaceScopeId, connectionInfo?.connectionType);
      toast({ variant: "success",
        title: 'Success',
        description: 'Razorpay disconnected successfully',
      });
      setDisconnectDialogOpen(false);
      resetState();
      await checkConnection();
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to disconnect Razorpay'));
      showApiError(err, { fallback: { title: 'Failed to disconnect Razorpay' } });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    if (!workspaceScopeId) return;

    setTesting(true);
    setError(null);
    try {
      const result = await razorpayApi.testConnection(workspaceScopeId);
      if (result.valid) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Connection test successful',
        });
      } else {
        showApiError(result, { fallback: { title: 'Connection test failed' } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Connection test failed' } });
    } finally {
      setTesting(false);
    }
  };

  const lastRefreshSignal = React.useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) return;
    lastRefreshSignal.current = refreshSignal;
    void checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const lastTestSignal = React.useRef(testSignal);
  useEffect(() => {
    if (testSignal === lastTestSignal.current) return;
    lastTestSignal.current = testSignal;
    void handleTestConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSignal]);

  const accountRows: IntegrationSyncRow[] = [
    { key: 'name', label: 'Account name', icon: Building2, value: connectionInfo?.accountName || null },
    { key: 'id', label: 'Account ID', icon: Hash, value: connectionInfo?.accountId || null },
    {
      key: 'type',
      label: 'Connection',
      icon: Key,
      value: connectionInfo?.connectionType === 'oauth' ? 'OAuth' : 'Manual',
    },
    {
      key: 'connected',
      label: 'Connected',
      icon: CalendarDays,
      value: connectionInfo?.connectedAt ? new Date(connectionInfo.connectedAt).toLocaleString() : null,
    },
    ...(connectionInfo?.expiresAt
      ? [
          {
            key: 'expires',
            label: 'Token expires',
            icon: CalendarClock,
            value: new Date(connectionInfo.expiresAt).toLocaleString(),
          },
        ]
      : []),
  ];

  const loadPayments = async () => {
    if (!workspaceScopeId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.listPayments(workspaceScopeId, {
        page: paymentsPage,
        limit: paymentsPageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      });
      setPayments(result.items || []);
      setPaymentsTotal(result.total || 0);
      setInitialLoadComplete(true); // Mark initial load as complete
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load payments' } });
      setInitialLoadComplete(true); // Mark as complete even on error
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSyncPayments = async () => {
    if (!workspaceScopeId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.syncPayments(workspaceScopeId, { count: 100 });
      const count = result.synced || 0;
      const errSuffix = result.errors > 0 ? ` (${result.errors} errors)` : '';
      toast(count > 0
        ? { title: 'Payments synced', description: `Synced ${count} ${count === 1 ? 'payment' : 'payments'} from Razorpay${errSuffix}.` }
        : { title: 'Up to date', description: `No new payments to sync from Razorpay${errSuffix}.` }
      );
      await loadPayments();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to sync payments' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSyncPaymentLinks = async () => {
    if (!workspaceScopeId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.syncPaymentLinks(workspaceScopeId, {
        count: 100,
        skip: 0,
      });
      const count = result.synced || 0;
      const errSuffix = result.errors > 0 ? ` (${result.errors} errors)` : '';
      toast(count > 0
        ? { title: 'Payment links synced', description: `Synced ${count} ${count === 1 ? 'link' : 'links'} from Razorpay${errSuffix}.` }
        : { title: 'Up to date', description: `No new payment links to sync from Razorpay${errSuffix}.` }
      );
      await loadPaymentLinks();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to sync payment links' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadPaymentLinks = async () => {
    if (!workspaceScopeId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.listPaymentLinks(workspaceScopeId, {});
      const all = result.payment_links || result.items || [];
      setPaymentLinks(all);
      setLinksTotal(all.length);
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load payment links' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const autoSyncData = async () => {
    if (!workspaceScopeId) return;

    toast({
      title: 'Syncing Data',
      description: 'Syncing payments and payment links...',
    });

    try {
      await razorpayApi.syncPayments(workspaceScopeId, { count: 100 });
      await razorpayApi.syncPaymentLinks(workspaceScopeId, { count: 100, skip: 0 });

      await Promise.all([
        loadPayments(),
        loadPaymentLinks()
      ]);

      toast({ variant: "success",
        title: 'Sync Complete',
        description: 'Payments and payment links have been synced successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Sync Warning',
        description: 'Connected successfully but failed to sync some data. You can try syncing manually.',
        variant: 'warning',
      });
    }
  };

  const handleCreatePaymentLink = async () => {
    if (!workspaceScopeId) return;

    const amountCheck = validatePaymentAmount(linkForm.amount);
    if (!amountCheck.ok) {
      toast({
        title: 'Invalid amount',
        description: amountCheck.error,
        variant: 'warning',
      });
      return;
    }
    const amountNum = amountCheck.value;

    setCreatingLink(true);
    try {
      const payload: any = {
        amount: amountNum,
        currency: linkForm.currency,
        description: linkForm.description || 'Payment Request',
        notify: {
          sms: linkForm.notifySms,
          email: linkForm.notifyEmail,
        },
        reminder_enable: linkForm.reminderEnable,
      };

      if (linkForm.customerName || linkForm.customerEmail || linkForm.customerContact) {
        payload.customer = {};
        if (linkForm.customerName) payload.customer.name = linkForm.customerName;
        if (linkForm.customerEmail) payload.customer.email = linkForm.customerEmail;
        if (linkForm.customerContact) payload.customer.contact = linkForm.customerContact;
      }

      const result = await razorpayApi.createPaymentLink(workspaceScopeId, payload);

      if (result.link || result.paymentLink) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Payment link created successfully',
        });
        setCreateLinkDialogOpen(false);
        setLinkForm({
          amount: '',
          currency: 'INR',
          description: '',
          customerName: '',
          customerEmail: '',
          customerContact: '',
          notifySms: true,
          notifyEmail: true,
          reminderEnable: true,
        });
        await loadPaymentLinks();
      }
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create payment link' } });
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ variant: "success",
      title: 'Copied',
      description: 'Payment link copied to clipboard',
    });
  };

  const loadPlans = async () => {
    if (!workspaceScopeId) return;
    setLoadingPlans(true);
    try {
      const skip = (plansPage - 1) * plansPageSize;
      const result = await razorpayApi.listPlans(workspaceScopeId, { count: plansPageSize + 1, skip });
      const items = result.items || [];
      const hasMore = items.length > plansPageSize;
      setPlans(hasMore ? items.slice(0, plansPageSize) : items);
      setPlansTotal(hasMore ? skip + plansPageSize + 1 : skip + items.length);
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load plans' } });
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!workspaceScopeId) return;

    const amountNum = parseFloat(planForm.amount);
    if (!planForm.name || !planForm.amount || isNaN(amountNum) || amountNum <= 0) {
      toast({
        title: 'Invalid input',
        description: 'Please enter a valid name and amount',
        variant: 'warning',
      });
      return;
    }

    setCreatingPlan(true);
    try {
      await razorpayApi.createPlan(workspaceScopeId, {
        period: planForm.period,
        interval: parseInt(planForm.interval) || 1,
        item: {
          name: planForm.name,
          amount: amountNum,
          currency: planForm.currency,
          description: planForm.description || undefined,
        },
      });

      toast({ variant: "success",
        title: 'Success',
        description: 'Plan created successfully',
      });
      setCreatePlanDialogOpen(false);
      setPlanForm({
        name: '',
        amount: '',
        currency: 'INR',
        description: '',
        period: 'monthly',
        interval: '1',
      });
      await loadPlans();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create plan' } });
    } finally {
      setCreatingPlan(false);
    }
  };

  const loadSubscriptions = async () => {
    if (!workspaceScopeId) return;
    setLoadingSubscriptions(true);
    try {
      const skip = (subsPage - 1) * subsPageSize;
      const result = await razorpayApi.listSubscriptions(workspaceScopeId, { count: subsPageSize + 1, skip });
      const items = result.items || [];
      const hasMore = items.length > subsPageSize;
      setSubscriptions(hasMore ? items.slice(0, subsPageSize) : items);
      setSubsTotal(hasMore ? skip + subsPageSize + 1 : skip + items.length);

      try {
        const offersRes: any = await razorpayApi.listOffers(workspaceScopeId);
        const offerItems = Array.isArray(offersRes?.items) ? offersRes.items : [];
        const map: Record<string, string> = {};
        for (const o of offerItems) {
          if (o?.id) map[o.id] = o.name || o.id;
        }
        setOffersById(map);
      } catch {
      }
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load subscriptions' } });
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!workspaceScopeId) return;

    if (!subForm.planId || !subForm.totalCount) {
      toast({
        title: 'Missing information',
        description: 'Please select a plan and enter total count',
        variant: 'warning',
      });
      return;
    }

    setCreatingSub(true);
    try {
      await razorpayApi.createSubscription(workspaceScopeId, {
        planId: subForm.planId,
        totalCount: parseInt(subForm.totalCount),
        quantity: parseInt(subForm.quantity) || 1,
        customerId: subForm.customerId || undefined,
        offerId: subForm.offerId.trim() ? subForm.offerId.trim() : undefined,
      });

      toast({ variant: "success",
        title: 'Success',
        description: 'Subscription created successfully',
      });
      setCreateSubDialogOpen(false);
      setSubForm({
        planId: '',
        totalCount: '',
        quantity: '1',
        customerId: '',
        offerId: 'none',
      });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create subscription' } });
    } finally {
      setCreatingSub(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!workspaceScopeId) return;
    try {
      await razorpayApi.cancelSubscription(workspaceScopeId, subscriptionId, false);
      toast({ variant: "success", title: 'Success', description: 'Subscription cancelled' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to cancel subscription' } });
    }
  };

  const handlePauseSubscription = async (subscriptionId: string) => {
    if (!workspaceScopeId) return;
    try {
      await razorpayApi.pauseSubscription(workspaceScopeId, subscriptionId);
      toast({ variant: "success", title: 'Success', description: 'Subscription paused' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to pause subscription' } });
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    if (!workspaceScopeId) return;
    try {
      await razorpayApi.resumeSubscription(workspaceScopeId, subscriptionId);
      toast({ variant: "success", title: 'Success', description: 'Subscription resumed' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to resume subscription' } });
    }
  };

  const getStatusBadge = (status: string) => <StatusPill meta={razorpayStatusMeta(status)} />;

  const linksTotalPages = Math.ceil(linksTotal / linksPageSize);
  const plansTotalPages = Math.ceil(plansTotal / plansPageSize);
  const subsTotalPages = Math.ceil(subsTotal / subsPageSize);

  const paginatedLinks = useMemo(() => {
    const start = (linksPage - 1) * linksPageSize;
    return paymentLinks.slice(start, start + linksPageSize);
  }, [paymentLinks, linksPage, linksPageSize]);

  useEffect(() => {
    if (linksTotalPages > 0 && linksPage > linksTotalPages) {
      setLinksPage(linksTotalPages);
    }
  }, [linksTotalPages, linksPage]);

  const paymentsColumns: Column<Payment>[] = useMemo(() => [
    {
      key: 'razorpay_payment_id',
      header: 'Payment ID',
      render: (_, payment) => (
        <div className="whitespace-nowrap font-mono text-[12px] text-[#475467]">
          {payment.razorpay_payment_id?.substring(0, 16) || payment.id?.substring(0, 16) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (_, payment) => {
        const name = payment.customer_name || payment.contact?.name || null;
        const email = payment.customer_email || payment.contact?.email || null;
        const phone = payment.customer_contact || payment.contact?.phone || null;
        const primary = name || email || phone;
        const secondary = name ? (email || phone) : (email && phone ? phone : null);
        if (!primary) return <span className="text-[#98A2B3]">—</span>;
        return (
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-[#101828]">{primary}</p>
            {secondary && <p className="truncate text-[12px] text-[#667085]">{secondary}</p>}
          </div>
        );
      },
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, payment) => (
        <div className="whitespace-nowrap font-semibold tabular-nums text-[#101828]">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: payment.currency || 'INR',
          }).format(Number(payment.amount) || 0)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, payment) => getStatusBadge(payment.status),
    },
    {
      key: 'payment_method',
      header: 'Method',
      render: (_, payment) => (
        <span className="whitespace-nowrap capitalize text-[#475467]">
          {payment.payment_method || payment.method || 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_, payment) => (
        <span className="whitespace-nowrap tabular-nums text-[#667085]">
          {(() => {
            const dateValue = payment.created_at;
            if (!dateValue) return 'N/A';
            const date = typeof dateValue === 'number'
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
            return date.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short',
            });
          })()}
        </span>
      ),
    },
  ], []);

  const paymentLinksColumns: Column<PaymentLink>[] = useMemo(() => [
    {
      key: 'short_url',
      header: 'Payment Link',
      render: (_, link) => (
        <div className="flex items-center gap-2">
          <a
            href={link.short_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 font-medium text-[#067647] transition-colors hover:underline"
          >
            {link.short_url}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-muted"
            onClick={() => handleCopyLink(link.short_url)}
            title="Copy link"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, link) => (
        <div className="whitespace-nowrap font-semibold tabular-nums text-[#101828]">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: link.currency || 'INR',
          }).format((link.amount || 0) / 100)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, link) => getStatusBadge(link.status),
    },
    {
      key: 'amount_paid',
      header: 'Paid',
      render: (_, link) => (
        <div className="whitespace-nowrap tabular-nums">
          {link.amount_paid !== undefined && link.amount_paid > 0 ? (
            <span className="font-semibold text-[#067647]">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: link.currency || 'INR',
              }).format((link.amount_paid || 0) / 100)}
            </span>
          ) : (
            <span className="text-[#98A2B3]">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (_, link) => (
        <span className="line-clamp-1 max-w-[220px] text-[#667085]">
          {link.description || 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_, link) => (
        <span className="whitespace-nowrap tabular-nums text-[#667085]">
          {(() => {
            const dateValue = link.created_at || link.updated_at;
            if (!dateValue) return 'N/A';
            const date = typeof dateValue === 'number'
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
            return date.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short',
            });
          })()}
        </span>
      ),
    },
  ], []);

  const plansColumns: Column<Plan>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Plan ID',
      render: (_, plan) => (
        <div className="whitespace-nowrap font-mono text-[12px] text-[#475467]">
          {plan.id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'item',
      header: 'Name',
      render: (_, plan) => (
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-[#101828]">{plan.item?.name || 'N/A'}</p>
          {plan.item?.description && (
            <p className="truncate text-[12px] text-[#667085]">{plan.item.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, plan) => (
        <div className="whitespace-nowrap font-semibold tabular-nums text-[#101828]">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: plan.item?.currency || 'INR',
          }).format((plan.item?.amount || 0) / 100)}
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Billing Cycle',
      render: (_, plan) => (
        <span className="whitespace-nowrap capitalize text-[#101828]">
          Every {plan.interval > 1 ? `${plan.interval} ` : ''}{plan.period}{plan.interval > 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_, plan) => (
        <span className="whitespace-nowrap tabular-nums text-[#667085]">
          {plan.created_at ? new Date(plan.created_at * 1000).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }) : 'N/A'}
        </span>
      ),
    },
  ], []);

  const subscriptionsColumns: Column<Subscription>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Subscription ID',
      render: (_, sub) => (
        <div className="whitespace-nowrap font-mono text-[12px] text-[#475467]">
          {sub.id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'plan_id',
      header: 'Plan',
      render: (_, sub) => (
        <div className="whitespace-nowrap font-mono text-[12px] text-[#667085]">
          {sub.plan_id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'offer_id',
      header: 'Offer',
      render: (_, sub) =>
        sub.offer_id ? (
          <span className="text-[#101828]" title={sub.offer_id}>
            {offersById[sub.offer_id] || sub.offer_id}
          </span>
        ) : (
          <span className="whitespace-nowrap tabular-nums text-[#667085]">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, sub) => getStatusBadge(sub.status),
    },
    {
      key: 'paid_count',
      header: 'Progress',
      render: (_, sub) => (
        <span className="whitespace-nowrap tabular-nums text-[#101828]">
          {sub.paid_count ?? 0} / {sub.total_count ?? '∞'} cycles
        </span>
      ),
    },
    {
      key: 'current_end',
      header: 'Current Period End',
      render: (_, sub) => (
        <span className="whitespace-nowrap tabular-nums text-[#667085]">
          {sub.current_end ? new Date(sub.current_end * 1000).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, sub) => (
        <div className="flex items-center justify-end gap-1">
          {sub.status === 'active' && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-yellow-700 hover:bg-yellow-50 transition-colors"
              onClick={() => handlePauseSubscription(sub.id)}
            >
              <Pause className="h-3 w-3 mr-1" />
              Pause
            </button>
          )}
          {sub.status === 'paused' && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-green-700 hover:bg-green-50 transition-colors"
              onClick={() => handleResumeSubscription(sub.id)}
            >
              <Play className="h-3 w-3 mr-1" />
              Resume
            </button>
          )}
          {['active', 'authenticated', 'created'].includes(sub.status) && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors"
              onClick={() => handleCancelSubscription(sub.id)}
            >
              <Ban className="h-3 w-3 mr-1" />
              Cancel
            </button>
          )}
          {['cancelled', 'completed', 'expired'].includes(sub.status) && (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
  ], []);

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className=" space-y-6">
      {!hideTabs && !embedded && (
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/home/integrations')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl sm:text-2xl">Razorpay Integration</CardTitle>
            <CardDescription className="text-sm">Connect your Razorpay account to process payments</CardDescription>
          </div>
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {!hideTabs && embedded && (
          <div className="border-b border-[#EAECF0]">
            <UnderlineTabs
              variant="underline"
              value={activeTab}
              onChange={setActiveTab}
              ariaLabel="Razorpay sections"
            >
              {RAZORPAY_TABS.map((t) => (
                <UnderlineTab key={t.id} value={t.id}>
                  {t.label}
                </UnderlineTab>
              ))}
            </UnderlineTabs>
          </div>
        )}
        {!hideTabs && !embedded && (
          <TabsList className='inline-flex gap-2 p-2 h-[46px] rounded-[20px]' >
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="connect">
              <span className="relative z-10">Connect</span>
              {activeTab === 'connect' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="payments"
              disabled={!isConnected}>
              <span className="relative z-10">Payments</span>
              {activeTab === 'payments' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="links"
              disabled={!isConnected}>
              <span className="relative z-10">Payment Links</span>
              {activeTab === 'links' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="plans"
              disabled={!isConnected}>
              <span className="relative z-10">Plans</span>
              {activeTab === 'plans' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="subscriptions"
              disabled={!isConnected}>
              <span className="relative z-10">Subscriptions</span>
              {activeTab === 'subscriptions' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="connect" className="">
          <Card className={embedded ? 'border-none bg-transparent shadow-none' : 'border-none'}>
            {!embedded && (
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{isConnected ? 'Connection Status' : 'Connect Razorpay'}</CardTitle>
                  <CardDescription>
                    {isConnected
                      ? 'Current Razorpay connection status'
                      : 'Enter your Razorpay API credentials'}
                  </CardDescription>
                </div>
                {isConnected && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkConnection}
                    disabled={loadingData || !workspaceScopeId}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingData ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            )}
            <CardContent className={embedded ? 'p-0' : undefined}>
              {loadingData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : embedded && isConnected ? (
                <div className="space-y-8">
                  <IntegrationDataSync
                    title="Account details"
                    rows={accountRows}
                    onNavigate={() => {}}
                  />
                  <RazorpayWebhookSetupSteps />
                </div>
              ) : embedded ? (
                <p className="py-10 text-center text-[13px] text-[#6A7282]">
                  Razorpay is not connected for this workspace. Use Connect above to link it.
                </p>
              ) : isConnected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <PlugZap className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                    <Badge variant="outline">
                      {connectionInfo?.connectionType === 'oauth' ? 'OAuth' : 'Manual'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Account ID</Label>
                      <p className="font-medium">{connectionInfo?.accountId || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Account Name</Label>
                      <p className="font-medium">{connectionInfo?.accountName || 'N/A'}</p>
                    </div>
                    {connectionInfo?.expiresAt && (
                      <div>
                        <Label className="text-muted-foreground">Token Expires</Label>
                        <p className="font-medium">
                          {new Date(connectionInfo.expiresAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {connectionInfo?.connectedAt && (
                      <div>
                        <Label className="text-muted-foreground">Connected At</Label>
                        <p className="font-medium">
                          {new Date(connectionInfo.connectedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={testing}
                    >
                      {testing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDisconnectDialogOpen(true)}
                      disabled={disconnecting}
                    >
                      {disconnecting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Unplug className="h-4 w-4 mr-2" />
                      )}
                      Disconnect
                    </Button>
                  </div>

                  <WebhookSetupCard variant="post-connect" />

                  <DisconnectConfirmDialog
                    open={disconnectDialogOpen}
                    onOpenChange={setDisconnectDialogOpen}
                    onConfirm={handleDisconnect}
                    isLoading={disconnecting}
                    integration="razorpay"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-4 md:py-10">
                  <div className="w-full space-y-8">
                    <div className="space-y-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="p-4 bg-brand/10 rounded-2xl border border-brand/20 shadow-sm shadow-brand/10">
                          <Key className="h-8 w-8 text-brand" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl md:text-4xl font-extrabold tracking-tight text-[#08090A]">
                          Connect Razorpay
                        </h3>
                        <p className="text-gray-500 text-sm md:text-base max-w-sm mx-auto leading-relaxed">
                          Securely accept payments via Credit Cards, UPI, and Netbanking in your store.
                        </p>
                      </div>
                    </div>

                    <Card className="border-none shadow-[0_0_10px_0_rgba(0,0,0,0.06)] rounded-[12px]">
                      <CardContent className="p-6 md:p-8">
                        <form onSubmit={handleManualConnect} className="space-y-6">
                          <div className="space-y-5">
                            <div className="space-y-2.5">
                              <Label htmlFor="keyId" className="text-sm font-semibold text-gray-700 ml-1">Key ID</Label>
                              <Input
                                id="keyId"
                                type="text"
                                placeholder="rzp_test_..."
                                value={form.keyId}
                                onChange={(e) => setForm({ ...form, keyId: e.target.value })}
                                required
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-brand focus:ring-brand/10 transition-all font-mono"
                              />
                            </div>
                            <div className="space-y-2.5">
                              <Label htmlFor="keySecret" className="text-sm font-semibold text-gray-700 ml-1">Key Secret</Label>
                              <Input
                                id="keySecret"
                                type="password"
                                placeholder="••••••••••••••••"
                                value={form.keySecret}
                                onChange={(e) => setForm({ ...form, keySecret: e.target.value })}
                                required
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-brand focus:ring-brand/10 transition-all"
                              />
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="w-[fit] h-12 bg-brand hover:bg-[#159a5b] text-white rounded-[12px] font-bold text-base shadow-lg shadow-brand/20 transition-all active:scale-[0.98] disabled:opacity-70"
                            disabled={connecting || !workspaceScopeId}
                          >
                            {connecting ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Connecting to Razorpay...
                              </>
                            ) : (
                              <>
                                <PlugZap className="h-5 w-5 mr-2" />
                                Connect Account
                              </>
                            )}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>

                    <div className="bg-white rounded-[12px] p-6 md:p-8 space-y-5 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
                      <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand">
                          <span className="font-bold text-sm">i</span>
                        </div>
                        <h4 className="font-bold text-base text-[#08090A]">
                          How to find your API credentials
                        </h4>
                      </div>

                      <ol className="space-y-4">
                        {[
                          { text: "Log in to your", link: "Razorpay Dashboard", url: "https://dashboard.razorpay.com" },
                          { text: "Navigate to Settings → API Keys sidebar menu" },
                          { text: "Click on Generate Key or Regenerate Key" },
                          { text: "Copy the Key ID and Key Secret and paste them above" }
                        ].map((step, idx) => (
                          <li key={idx} className="flex gap-4 text-sm text-gray-600 group">
                            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 text-gray-400 font-bold group-hover:bg-brand/5 group-hover:text-brand transition-colors">
                              {idx + 1}
                            </span>
                            <p className="pt-0.5 leading-relaxed">
                              {step.text}{" "}
                              {step.link && (
                                <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-brand font-bold hover:underline">
                                  {step.link}
                                </a>
                              )}
                            </p>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <WebhookSetupCard variant="pre-connect" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-0">
          <div className="flex flex-wrap items-center gap-2.5 py-3">
            <ToolbarSearch
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v);
                setPaymentsPage(1);
              }}
              placeholder="Search payments, customer…"
              testId="razorpay-payments-search"
            />
            <BoardFilterDropdown
              label="Status"
              options={PAYMENT_STATUS_OPTIONS}
              selected={statusFilter === 'all' ? [] : statusFilter.split(',').filter(Boolean)}
              onChange={(next) => {
                setStatusFilter(next.length ? next.join(',') : 'all');
                setPaymentsPage(1);
              }}
              searchable={false}
              clearLabel="Clear all"
            />
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <ToolbarMoreMenu
                label="More payment actions"
                items={[
                  {
                    label: loadingPayments ? 'Syncing…' : 'Sync payments',
                    icon: RefreshCw,
                    onSelect: () => void handleSyncPayments(),
                    disabled: loadingPayments || !workspaceScopeId,
                    spinning: loadingPayments,
                  },
                ]}
              />
            </div>
          </div>
          <TableShell className="px-0 pt-0 pb-0">
            <DataTable
              variant="cardTheme"
              paginationStyle="crm"
              scrollbars="visible"
              innerScroll={false}
              data={payments}
              columns={paymentsColumns}
              loading={loadingPayments || loadingData || !initialLoadComplete}
              currentPage={paymentsPage}
              pageSize={paymentsPageSize}
              totalItems={paymentsTotal}
              onPageChange={setPaymentsPage}
              onPageSizeChange={(size) => {
                setPaymentsPageSize(size);
                setPaymentsPage(1);
              }}
              pageSizeOptions={[10, 20]}
              showPagination
              emptyMessage={
                <EmptyTable
                  icon={CreditCard}
                  title={searchTerm || statusFilter !== 'all' ? 'No payments match' : 'No payments yet'}
                  detail={
                    searchTerm || statusFilter !== 'all'
                      ? 'Try adjusting your search or filters.'
                      : 'Sync payments from Razorpay to see them here.'
                  }
                  action={
                    !searchTerm && statusFilter === 'all' ? (
                      <CrmButton
                        variant="outline"
                        size="sm"
                        onClick={() => void handleSyncPayments()}
                        disabled={loadingPayments || !workspaceScopeId}
                      >
                        Sync payments
                      </CrmButton>
                    ) : undefined
                  }
                />
              }
            />
          </TableShell>
        </TabsContent>

        <TabsContent value="links" className="mt-0">
          <div className="flex flex-wrap items-center gap-2.5 py-3">
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <CrmButton
                variant="primary"
                size="sm"
                onClick={() => setCreateLinkDialogOpen(true)}
                disabled={!workspaceScopeId}
              >
                Create link
              </CrmButton>
            </div>
          </div>
          <TableShell className="px-0 pt-0 pb-0">
            <DataTable
              variant="cardTheme"
              paginationStyle="crm"
              scrollbars="visible"
              innerScroll={false}
              data={paginatedLinks}
              columns={paymentLinksColumns}
              loading={loadingPayments}
              currentPage={linksPage}
              pageSize={linksPageSize}
              totalItems={linksTotal}
              onPageChange={setLinksPage}
              onPageSizeChange={(size) => {
                setLinksPageSize(size);
                setLinksPage(1);
              }}
              pageSizeOptions={[10, 20]}
              showPagination
              emptyMessage={
                <EmptyTable
                  icon={LinkIcon}
                  title="No payment links yet"
                  detail="Create a payment link to start accepting payments."
                />
              }
            />
          </TableShell>
        </TabsContent>

        <TabsContent value="plans" className="mt-0">
          <div className="flex flex-wrap items-center gap-2.5 py-3">
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <CrmButton
                variant="primary"
                size="sm"
                onClick={() => setCreatePlanDialogOpen(true)}
                disabled={!workspaceScopeId}
              >
                Create plan
              </CrmButton>
              <ToolbarMoreMenu
                label="More plan actions"
                items={[
                  {
                    label: 'Refresh',
                    icon: RefreshCw,
                    onSelect: () => void loadPlans(),
                    disabled: loadingPlans || !workspaceScopeId,
                    spinning: loadingPlans,
                  },
                ]}
              />
            </div>
          </div>
          <TableShell className="px-0 pt-0 pb-0">
            <DataTable
              variant="cardTheme"
              scrollbars="visible"
              innerScroll={false}
              data={plans}
              columns={plansColumns}
              loading={loadingPlans}
              showPagination={false}
              emptyMessage={
                <EmptyTable
                  icon={Package}
                  title="No plans yet"
                  detail="Create a plan to start billing customers on a recurring basis."
                />
              }
            />
            {plans.length > 0 && (
              <ContactsPagination
                currentPage={plansPage}
                totalPages={plansTotalPages}
                totalCount={plansTotal}
                pageSize={plansPageSize}
                onPageChange={setPlansPage}
                onPageSizeChange={(size) => {
                  setPlansPageSize(size);
                  setPlansPage(1);
                }}
                isLoading={loadingPlans}
                itemLabel="plans"
                pageSizeOptions={[10, 20]}
                minPageSize={10}
                hideTotalCount
                className="bg-white"
              />
            )}
          </TableShell>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-0">
          <div className="flex flex-wrap items-center gap-2.5 py-3">
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <CrmButton
                variant="primary"
                size="sm"
                onClick={() => {
                  if (plans.length === 0) loadPlans();
                  setCreateSubDialogOpen(true);
                }}
                disabled={!workspaceScopeId}
              >
                Create subscription
              </CrmButton>
              <ToolbarMoreMenu
                label="More subscription actions"
                items={[
                  {
                    label: 'Refresh',
                    icon: RefreshCw,
                    onSelect: () => void loadSubscriptions(),
                    disabled: loadingSubscriptions || !workspaceScopeId,
                    spinning: loadingSubscriptions,
                  },
                ]}
              />
            </div>
          </div>
          <TableShell className="px-0 pt-0 pb-0">
            <DataTable
              variant="cardTheme"
              scrollbars="visible"
              innerScroll={false}
              data={subscriptions}
              columns={subscriptionsColumns}
              loading={loadingSubscriptions}
              showPagination={false}
              emptyMessage={
                <EmptyTable
                  icon={CalendarClock}
                  title="No subscriptions yet"
                  detail="Create a subscription to bill a customer on a recurring basis."
                />
              }
            />
            {subscriptions.length > 0 && (
              <ContactsPagination
                currentPage={subsPage}
                totalPages={subsTotalPages}
                totalCount={subsTotal}
                pageSize={subsPageSize}
                onPageChange={setSubsPage}
                onPageSizeChange={(size) => {
                  setSubsPageSize(size);
                  setSubsPage(1);
                }}
                isLoading={loadingSubscriptions}
                itemLabel="subscriptions"
                pageSizeOptions={[10, 20]}
                minPageSize={10}
                hideTotalCount
                className="bg-white"
              />
            )}
          </TableShell>
        </TabsContent>

      </Tabs >

      <FloatingDrawer
        open={createLinkDialogOpen}
        onOpenChange={setCreateLinkDialogOpen}
        width={520}
        title="Create payment link"
      >
        <FloatingDrawerHeader title="Create payment link" subtitle="A link to share with a customer" />
        <FloatingDrawerBody className="space-y-4">
          <FormField label="Amount" required>
            <div className="flex gap-2">
              <input
                autoFocus
                type="number"
                step="0.01"
                min="0.01"
                max={MAX_PAYMENT_LINK_AMOUNT}
                placeholder="0.00"
                value={linkForm.amount}
                onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                aria-invalid={!!linkAmountError}
                className={cn(
                  crmInputClass,
                  'min-w-0 flex-1',
                  linkAmountError && 'border-[#F04438] focus:border-[#F04438]',
                )}
              />
              <CrmDropdown
                value={linkForm.currency}
                options={CURRENCY_OPTIONS}
                onChange={(v) => setLinkForm({ ...linkForm, currency: v })}
                className="w-[100px] shrink-0"
              />
            </div>
            {linkAmountError ? (
              <p className="mt-1 text-[11.5px] text-[#B42318]">{linkAmountError}</p>
            ) : (
              <p className="mt-1 text-[11.5px] text-[#667085]">
                Up to {MAX_PAYMENT_LINK_AMOUNT.toLocaleString('en-IN')} {linkForm.currency}.
              </p>
            )}
          </FormField>

          <FormField label="Description">
            <textarea
              placeholder="Optional"
              value={linkForm.description}
              onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
              className={cn(crmInputClass, 'h-20 resize-none py-2')}
            />
          </FormField>

          <FormField label="Customer name">
            <input
              placeholder="Optional"
              value={linkForm.customerName}
              onChange={(e) => setLinkForm({ ...linkForm, customerName: e.target.value })}
              className={crmInputClass}
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Customer email">
              <input
                type="email"
                placeholder="Optional"
                value={linkForm.customerEmail}
                onChange={(e) => setLinkForm({ ...linkForm, customerEmail: e.target.value })}
                className={crmInputClass}
              />
            </FormField>
            <FormField label="Customer phone">
              <input
                placeholder="Optional"
                value={linkForm.customerContact}
                onChange={(e) => setLinkForm({ ...linkForm, customerContact: e.target.value })}
                className={crmInputClass}
              />
            </FormField>
          </div>

          <section>
            <h3 className="mb-2 text-[13px] font-semibold text-[#101828]">Notifications</h3>
            <div className="space-y-2.5">
              {(
                [
                  ['link-notify-sms', 'Send SMS notification', 'notifySms'],
                  ['link-notify-email', 'Send email notification', 'notifyEmail'],
                  ['link-reminder', 'Send payment reminders', 'reminderEnable'],
                ] as const
              ).map(([id, text, key]) => (
                <label key={id} htmlFor={id} className="flex cursor-pointer items-center gap-2.5">
                  <Checkbox
                    id={id}
                    checked={linkForm[key]}
                    onCheckedChange={(checked) => setLinkForm({ ...linkForm, [key]: checked === true })}
                  />
                  <span className="text-[13px] text-[#344054]">{text}</span>
                </label>
              ))}
            </div>
          </section>
        </FloatingDrawerBody>
        <FloatingDrawerFooter>
          <CrmButton
            variant="outline"
            size="sm"
            onClick={() => setCreateLinkDialogOpen(false)}
            disabled={creatingLink}
          >
            Cancel
          </CrmButton>
          <CrmButton
            size="sm"
            onClick={handleCreatePaymentLink}
            disabled={creatingLink || !linkForm.amount || !linkAmountCheck.ok}
          >
            {creatingLink ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              'Create link'
            )}
          </CrmButton>
        </FloatingDrawerFooter>
      </FloatingDrawer>

      <FloatingDrawer
        open={createPlanDialogOpen}
        onOpenChange={setCreatePlanDialogOpen}
        width={520}
        title="Create plan"
      >
        <FloatingDrawerHeader title="Create plan" subtitle="A recurring billing plan" />
        <FloatingDrawerBody className="space-y-4">
          <FormField label="Plan name" required>
            <input
              autoFocus
              placeholder="e.g. Monthly Pro Plan"
              value={planForm.name}
              onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
              className={crmInputClass}
            />
          </FormField>

          <FormField label="Amount" required>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={planForm.amount}
                onChange={(e) => setPlanForm({ ...planForm, amount: e.target.value })}
                className={cn(crmInputClass, 'min-w-0 flex-1')}
              />
              <CrmDropdown
                value={planForm.currency}
                options={CURRENCY_OPTIONS}
                onChange={(v) => setPlanForm({ ...planForm, currency: v })}
                className="w-[100px] shrink-0"
              />
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Billing period" required>
              <CrmDropdown
                value={planForm.period}
                options={PLAN_PERIOD_OPTIONS}
                onChange={(v) => setPlanForm({ ...planForm, period: v as typeof planForm.period })}
              />
            </FormField>
            <FormField label="Interval">
              <input
                type="number"
                min="1"
                placeholder="1"
                value={planForm.interval}
                onChange={(e) => setPlanForm({ ...planForm, interval: e.target.value })}
                className={crmInputClass}
              />
            </FormField>
          </div>

          <FormField label="Description">
            <textarea
              placeholder="Optional"
              value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
              className={cn(crmInputClass, 'h-20 resize-none py-2')}
            />
          </FormField>
        </FloatingDrawerBody>
        <FloatingDrawerFooter>
          <CrmButton
            variant="outline"
            size="sm"
            onClick={() => setCreatePlanDialogOpen(false)}
            disabled={creatingPlan}
          >
            Cancel
          </CrmButton>
          <CrmButton
            size="sm"
            onClick={handleCreatePlan}
            disabled={creatingPlan || !planForm.name || !planForm.amount}
          >
            {creatingPlan ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              'Create plan'
            )}
          </CrmButton>
        </FloatingDrawerFooter>
      </FloatingDrawer>

      <FloatingDrawer
        open={createSubDialogOpen}
        onOpenChange={setCreateSubDialogOpen}
        width={520}
        title="Create subscription"
      >
        <FloatingDrawerHeader title="Create subscription" subtitle="Bill a customer on a recurring plan" />
        <FloatingDrawerBody className="space-y-4">
          <FormField label="Plan" required>
            <CrmDropdown
              value={subForm.planId}
              placeholder={plans.length === 0 ? 'No plans yet' : 'Select a plan'}
              disabled={plans.length === 0}
              options={plans.map((plan) => ({
                value: plan.id,
                label: `${plan.item?.name || plan.id} · ${new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: plan.item?.currency || 'INR',
                }).format((plan.item?.amount || 0) / 100)} / ${plan.period}`,
              }))}
              onChange={(v) => setSubForm({ ...subForm, planId: v })}
            />
            {plans.length === 0 ? (
              <p className="mt-1 text-[11.5px] text-[#667085]">Create a plan on the Plans tab first.</p>
            ) : null}
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Total billing cycles" required>
              <input
                type="number"
                min="1"
                placeholder="e.g. 12"
                value={subForm.totalCount}
                onChange={(e) => setSubForm({ ...subForm, totalCount: e.target.value })}
                className={crmInputClass}
              />
            </FormField>
            <FormField label="Quantity">
              <input
                type="number"
                min="1"
                placeholder="1"
                value={subForm.quantity}
                onChange={(e) => setSubForm({ ...subForm, quantity: e.target.value })}
                className={crmInputClass}
              />
            </FormField>
          </div>

          <FormField label="Offer ID">
            <input
              placeholder="offer_xxxxxxxxxxxxx"
              value={subForm.offerId}
              onChange={(e) => setSubForm({ ...subForm, offerId: e.target.value })}
              className={crmInputClass}
            />
            <p className="mt-1 text-[11.5px] text-[#667085]">
              Optional. Paste an Offer ID from the Razorpay Dashboard (Subscriptions → Offers) to apply a discount.
            </p>
          </FormField>

          <FormField label="Customer ID">
            <input
              placeholder="cust_xxxxxxxxxxxxx"
              value={subForm.customerId}
              onChange={(e) => setSubForm({ ...subForm, customerId: e.target.value })}
              className={crmInputClass}
            />
          </FormField>
        </FloatingDrawerBody>
        <FloatingDrawerFooter>
          <CrmButton
            variant="outline"
            size="sm"
            onClick={() => setCreateSubDialogOpen(false)}
            disabled={creatingSub}
          >
            Cancel
          </CrmButton>
          <CrmButton
            size="sm"
            onClick={handleCreateSubscription}
            disabled={creatingSub || !subForm.planId || !subForm.totalCount}
          >
            {creatingSub ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Creating…
              </>
            ) : (
              'Create subscription'
            )}
          </CrmButton>
        </FloatingDrawerFooter>
      </FloatingDrawer>
    </div >
  );
};

export default RazorpayPage;

